import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Save, Wand2 } from "lucide-react";

const FMONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const METRICS = [
  { key: "revenue", label: "Hotel Revenue ($)" },
  { key: "adr", label: "ADR ($)" },
  { key: "occupancy", label: "Occupancy (%)" },
  { key: "revpar", label: "RevPAR ($)" },
];
const round2 = (v) => {
  const n = Number(String(v).replace(/[$,%\s]/g, ""));
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
};

export function MonthlyFinancials({ propertyId, canEdit }) {
  const [year, setYear] = useState(new Date().getFullYear() - 1);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!propertyId) { setRows([]); return; }
    api.get("/monthly-financials", { params: { property_id: propertyId, year } })
      .then((r) => setRows(r.data.rows)).catch(() => setRows([]));
  }, [propertyId, year]);
  useEffect(() => { load(); }, [load]);

  const setCell = (m, k, v) => setRows((rs) => rs.map((r) => (r.month === m ? { ...r, [k]: v } : r)));

  // Paste a block from Excel starting at (month index, metric index).
  const onPaste = (e, mIndex, cIndex) => {
    const text = e.clipboardData.getData("text");
    if (!text || (!text.includes("\t") && !text.includes("\n"))) return; // single value: default paste
    e.preventDefault();
    const grid = text.replace(/\r/g, "").split("\n")
      .filter((l, i, a) => !(i === a.length - 1 && l === "")).map((l) => l.split("\t"));
    setRows((rs) => {
      const next = rs.map((r) => ({ ...r }));
      grid.forEach((line, r) => {
        const ri = mIndex + r;
        if (ri < 0 || ri >= next.length) return;
        line.forEach((val, c) => {
          const col = METRICS[cIndex + c];
          if (!col) return;
          next[ri][col.key] = String(val).replace(/[$,%\s]/g, "");
        });
      });
      return next;
    });
    toast.success(`Pasted ${grid.length} row${grid.length > 1 ? "s" : ""}`);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = rows.map((r) => ({
        month: r.month, revenue: round2(r.revenue), adr: round2(r.adr),
        occupancy: round2(r.occupancy), revpar: round2(r.revpar),
      }));
      await api.put("/monthly-financials", { rows: payload }, { params: { property_id: propertyId, year } });
      setRows((rs) => rs.map((r) => ({
        ...r, revenue: round2(r.revenue), adr: round2(r.adr), occupancy: round2(r.occupancy), revpar: round2(r.revpar),
      })));
      toast.success(`${year} monthly data saved`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const autofill = async () => {
    setBusy(true);
    try {
      const r = await api.post("/monthly-financials/autofill", null, { params: { property_id: propertyId, year } });
      toast.success(`Auto-filled ${r.data.months_filled} month(s) from daily reports`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="monthly-financials">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-heading text-lg font-bold tracking-tight">Monthly Entry (Prior Years)</h3>
          <p className="text-sm text-muted-foreground">
            Enter monthly Occupancy, ADR, RevPAR &amp; Hotel Revenue for past years. Feeds the Owner Dashboard and Monthly Report.
            Tip: copy a block of cells from Excel and paste into the first cell.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-24" data-testid="fin-year" />
          {canEdit && <Button variant="outline" size="sm" onClick={autofill} disabled={busy || !propertyId} data-testid="fin-autofill-btn"><Wand2 className="mr-2 h-4 w-4" />Auto-fill from Daily</Button>}
          {canEdit && <Button size="sm" onClick={save} disabled={busy || !propertyId} data-testid="fin-save-btn">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Save {year}</Button>}
        </div>
      </div>
      <div className="mt-4 overflow-auto border border-border">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              <th className="border border-border bg-secondary px-3 py-2 text-left text-xs font-semibold">Month</th>
              {METRICS.map((m) => (
                <th key={m.key} className="border border-border bg-secondary px-3 py-2 text-left text-xs font-semibold">{m.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, mi) => (
              <tr key={r.month} data-testid={`fin-row-${r.month}`}>
                <td className="border border-border px-3 py-1.5 text-xs font-medium">{FMONTHS[r.month - 1]}</td>
                {METRICS.map((m, ci) => (
                  <td key={m.key} className="border border-border p-0">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={r[m.key] ?? ""}
                      disabled={!canEdit}
                      onChange={(e) => setCell(r.month, m.key, e.target.value)}
                      onPaste={(e) => onPaste(e, mi, ci)}
                      onBlur={(e) => e.target.value !== "" && setCell(r.month, m.key, round2(e.target.value))}
                      className="w-full bg-transparent px-3 py-1.5 text-right font-mono text-xs outline-none focus:bg-accent/40"
                      data-testid={`fin-${m.key}-${r.month}`}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
